import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import {
  Brain, Building2, Trees, BadgePoundSterling, ClipboardList, HardHat,
  BookOpen, ChevronDown, ChevronRight, Loader2, AlertTriangle, ExternalLink, MapPin, TrendingUp
} from "lucide-react";
import ContractorHealthCard from "@/components/admin/ContractorHealthCard";

function fmt(n) {
  if (n == null) return "—";
  return `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const days = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/**
 * IntelligencePanel — inline contextual intel for any case-like record.
 *
 * Props (all optional except admin-key being in adminApi):
 *   parkName, town, county, contractorName, typeOfWorks, description, companyName, excludeCaseId
 *   compact?: boolean — render the tight side-rail variant (used by ReportEditor)
 */
export default function IntelligencePanel({
  parkName,
  town,
  county,
  contractorName,
  typeOfWorks,
  description,
  companyName,
  excludeCaseId,
  compact = false,
}) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSection, setOpenSection] = useState({
    operator: true,
    park: true,
    benchmarks: true,
    similar: !compact,
    contractor: !compact,
    guidance: !compact,
  });

  useEffect(() => {
    // If we have nothing to match on, skip the call
    if (!parkName && !contractorName && !typeOfWorks && !companyName && !description) {
      setCtx(null);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    if (parkName) params.set("park_name", parkName);
    if (town) params.set("town", town);
    if (county) params.set("county", county);
    if (contractorName) params.set("contractor_name", contractorName);
    if (typeOfWorks) params.set("type_of_works", typeOfWorks);
    if (description) params.set("description", description);
    if (companyName) params.set("company_name", companyName);
    if (excludeCaseId) params.set("exclude_case_id", excludeCaseId);

    adminApi.get(`/intelligence/context?${params.toString()}`)
      .then((r) => setCtx(r.data))
      .catch(() => setCtx(null))
      .finally(() => setLoading(false));
  }, [parkName, town, county, contractorName, typeOfWorks, description, companyName, excludeCaseId]);

  if (loading) {
    return (
      <div className="bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-4 flex items-center gap-2 text-sm text-[#475569]" data-testid="intel-panel-loading">
        <Loader2 className="animate-spin" size={14}/> Surfacing intelligence…
      </div>
    );
  }
  if (!ctx) return null;

  const noResults =
    !ctx.operator && !ctx.park &&
    (ctx.repair_cost_benchmarks?.length ?? 0) === 0 &&
    (ctx.similar_cases?.length ?? 0) === 0 &&
    (ctx.contractor_history?.total_cases ?? 0) === 0 &&
    (ctx.guidance_links?.length ?? 0) === 0;
  if (noResults) {
    return (
      <div className="bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-4 text-xs text-[#475569]" data-testid="intel-panel-empty">
        <span className="inline-flex items-center gap-1.5"><Brain size={14} className="text-[#0F766E]"/>No matching intelligence found for this case yet.</span>
      </div>
    );
  }

  const toggle = (k) => setOpenSection((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="bg-gradient-to-br from-[#F7F4ED] to-white border border-[#0F766E]/20 rounded-xl overflow-hidden" data-testid="intel-panel">
      <div className="px-4 py-3 bg-[#071A33] text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-[#FFE7B6]"/>
          <h3 className="text-sm font-medium tracking-[0.18em] uppercase">Intelligence</h3>
        </div>
        <span className="text-[10px] text-white/60 tracking-[0.18em] uppercase">Auto-matched</span>
      </div>

      <div className="divide-y divide-[#E2DCD0]">
        {/* OPERATOR */}
        {ctx.operator && (
          <Section
            icon={<Building2 size={14}/>}
            title="Operator profile"
            subtitle={ctx.operator.name}
            badge={ctx.matched_by?.operator}
            open={openSection.operator}
            onToggle={() => toggle("operator")}
            testid="intel-section-operator"
          >
            <div className="text-sm space-y-2">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">
                {ctx.operator.headquarters && <span className="flex items-center gap-1"><MapPin size={11} className="text-[#B88A2A]"/>{ctx.operator.headquarters}</span>}
                {ctx.operator.parks_count_estimate && <span>≈ {ctx.operator.parks_count_estimate} parks</span>}
                {ctx.operator.year_founded && <span>Founded {ctx.operator.year_founded}</span>}
              </div>
              {ctx.operator.summary && (
                <p className="text-[#334155] leading-relaxed text-[13px]">{ctx.operator.summary}</p>
              )}
              {ctx.operator.key_facts?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {ctx.operator.key_facts.slice(0, 3).map((k, i) => (
                    <li key={`op-fact-${k}-${i}`} className="text-xs text-[#071A33] flex gap-1.5"><span className="text-[#B88A2A]">●</span>{k}</li>
                  ))}
                </ul>
              )}
              {ctx.operator.website && (
                <a href={ctx.operator.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0F766E] hover:underline mt-1">
                  Operator website <ExternalLink size={11}/>
                </a>
              )}
            </div>
          </Section>
        )}

        {/* PARK */}
        {ctx.park && (
          <Section
            icon={<Trees size={14}/>}
            title="Park profile"
            subtitle={ctx.park.name}
            badge={ctx.matched_by?.park}
            open={openSection.park}
            onToggle={() => toggle("park")}
            testid="intel-section-park"
          >
            <div className="text-sm space-y-2">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">
                <span className="flex items-center gap-1"><MapPin size={11} className="text-[#B88A2A]"/>{[ctx.park.town, ctx.park.county].filter(Boolean).join(", ")}</span>
                {ctx.park.operator_name && <span className="flex items-center gap-1"><Building2 size={11} className="text-[#0F766E]"/>{ctx.park.operator_name}</span>}
                {ctx.park.pitches_count && <span>{ctx.park.pitches_count} pitches</span>}
                <span className="flex gap-1">
                  {ctx.park.residential && <span className="bg-[#064E2E]/10 text-[#064E2E] px-1.5 py-0.5 rounded text-[10px]">Residential</span>}
                  {ctx.park.holiday && <span className="bg-[#B88A2A]/15 text-[#B88A2A] px-1.5 py-0.5 rounded text-[10px]">Holiday</span>}
                </span>
              </div>
              {ctx.park.summary && <p className="text-[#334155] leading-relaxed text-[13px]">{ctx.park.summary}</p>}
              {ctx.park.key_facts?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {ctx.park.key_facts.slice(0, 3).map((k, i) => (
                    <li key={`park-fact-${k}-${i}`} className="text-xs text-[#071A33] flex gap-1.5"><span className="text-[#B88A2A]">●</span>{k}</li>
                  ))}
                </ul>
              )}
            </div>
          </Section>
        )}

        {/* BENCHMARKS */}
        {ctx.repair_cost_benchmarks?.length > 0 && (
          <Section
            icon={<BadgePoundSterling size={14}/>}
            title="Repair cost benchmarks"
            subtitle={`${ctx.repair_cost_benchmarks.length} matched · ${ctx.matched_categories.join(" · ")}`}
            open={openSection.benchmarks}
            onToggle={() => toggle("benchmarks")}
            testid="intel-section-benchmarks"
          >
            <div className="space-y-2.5">
              {ctx.repair_cost_benchmarks.map((b) => (
                <div key={b.id} className="bg-white border border-[#E2DCD0] rounded-lg p-3" data-testid={`intel-benchmark-${b.slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-[#071A33] leading-tight">{b.title}</p>
                      <p className="text-[10px] text-[#0F766E] uppercase tracking-[0.12em] mt-0.5">{b.category}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display text-base text-[#071A33] tabular-nums leading-none">{fmt(b.typical_gbp)}</p>
                      <p className="text-[10px] text-[#475569] tabular-nums mt-0.5">{fmt(b.low_gbp)}–{fmt(b.high_gbp)}</p>
                    </div>
                  </div>
                  {b.red_flags?.length > 0 && (
                    <div className="mt-2 text-[11px] text-[#7A1F1F] bg-[#FCA5A5]/15 border border-[#FCA5A5]/40 rounded px-2 py-1.5 flex gap-1.5">
                      <AlertTriangle size={11} className="shrink-0 mt-0.5"/>
                      <span>{b.red_flags[0]}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* SIMILAR CASES */}
        {ctx.similar_cases?.length > 0 && (
          <Section
            icon={<ClipboardList size={14}/>}
            title="Historical similar cases"
            subtitle={`${ctx.similar_cases.length} prior case${ctx.similar_cases.length === 1 ? "" : "s"} on same park or town`}
            open={openSection.similar}
            onToggle={() => toggle("similar")}
            testid="intel-section-similar"
          >
            <div className="space-y-1.5">
              {ctx.similar_cases.map((c) => (
                <div key={c.id} className="text-[13px] flex items-start justify-between gap-3 py-1.5 border-b border-[#E2DCD0]/60 last:border-0">
                  <div className="min-w-0">
                    <p className="text-[#071A33] truncate">
                      <span className="text-[10px] text-[#475569] uppercase tracking-[0.1em] mr-1.5">{c.case_type}</span>
                      {c.full_name || "—"}
                    </p>
                    <p className="text-[11px] text-[#475569] truncate">
                      {[c.park_name, c.town_location].filter(Boolean).join(", ")}
                      {c.contractor_name && ` · ${c.contractor_name}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {c.quote_value != null && <p className="text-[12px] tabular-nums text-[#071A33]">{fmt(c.quote_value)}</p>}
                    <p className="text-[10px] text-[#475569]">{relTime(c.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* CONTRACTOR HEALTH (replaces simple "performance" section when contractor exists) */}
        {ctx.contractor_health && (
          <Section
            icon={<HardHat size={14}/>}
            title="Contractor health"
            subtitle={`${ctx.contractor_health.name}${ctx.contractor_history?.total_cases ? ` · ${ctx.contractor_history.total_cases} prior case${ctx.contractor_history.total_cases === 1 ? "" : "s"}` : ""}`}
            open={openSection.contractor}
            onToggle={() => toggle("contractor")}
            testid="intel-section-contractor"
            warning={ctx.contractor_health.effective_status === "RESTRICTED" || ctx.contractor_health.effective_status === "MONITOR"}
          >
            <ContractorHealthCard contractor={ctx.contractor_health} onUpdated={(c) => setCtx((p) => ({ ...p, contractor_health: c }))}/>
            {ctx.contractor_history?.recent?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] font-medium">Recent cases involving this contractor</p>
                {ctx.contractor_history.recent.map((c) => (
                  <div key={c.id} className="text-[12px] flex items-start justify-between gap-3 py-1.5 border-b border-[#E2DCD0]/60 last:border-0">
                    <div className="min-w-0">
                      <p className="text-[#071A33] truncate">{c.park_name || "—"}{c.town_location && <span className="text-[#475569]"> · {c.town_location}</span>}</p>
                      <p className="text-[11px] text-[#475569] truncate">{c.type_of_works || c.case_type}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {c.quote_value != null && <p className="text-[11px] tabular-nums text-[#071A33]">{fmt(c.quote_value)}</p>}
                      <p className="text-[10px] text-[#475569]">{relTime(c.created_at)}</p>
                    </div>
                  </div>
                ))}
                {ctx.contractor_history.total_cases > ctx.contractor_history.recent.length && (
                  <p className="text-[11px] text-[#475569] italic flex items-center gap-1 pt-1"><TrendingUp size={11}/>+{ctx.contractor_history.total_cases - ctx.contractor_history.recent.length} earlier case(s)</p>
                )}
              </div>
            )}
          </Section>
        )}

        {/* CONTRACTOR HISTORY (only when no contractor record yet — extreme edge case) */}
        {!ctx.contractor_health && ctx.contractor_history?.total_cases > 0 && (
          <Section
            icon={<HardHat size={14}/>}
            title="Contractor performance"
            subtitle={`${ctx.contractor_history.total_cases} prior case${ctx.contractor_history.total_cases === 1 ? "" : "s"} involving this contractor`}
            open={openSection.contractor}
            onToggle={() => toggle("contractor")}
            testid="intel-section-contractor"
            warning={ctx.contractor_history.total_cases >= 3}
          >
            <div className="space-y-1.5">
              {ctx.contractor_history.recent.map((c) => (
                <div key={c.id} className="text-[13px] flex items-start justify-between gap-3 py-1.5 border-b border-[#E2DCD0]/60 last:border-0">
                  <div className="min-w-0">
                    <p className="text-[#071A33] truncate">{c.park_name || "—"}{c.town_location && <span className="text-[#475569]"> · {c.town_location}</span>}</p>
                    <p className="text-[11px] text-[#475569] truncate">{c.type_of_works || c.case_type}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {c.quote_value != null && <p className="text-[12px] tabular-nums text-[#071A33]">{fmt(c.quote_value)}</p>}
                    <p className="text-[10px] text-[#475569]">{relTime(c.created_at)}</p>
                  </div>
                </div>
              ))}
              {ctx.contractor_history.total_cases > ctx.contractor_history.recent.length && (
                <p className="text-[11px] text-[#475569] italic flex items-center gap-1 pt-1"><TrendingUp size={11}/>+{ctx.contractor_history.total_cases - ctx.contractor_history.recent.length} earlier case(s)</p>
              )}
            </div>
          </Section>
        )}

        {/* GUIDANCE */}
        {ctx.guidance_links?.length > 0 && (
          <Section
            icon={<BookOpen size={14}/>}
            title="Relevant guidance notes"
            subtitle={`${ctx.guidance_links.length} matched`}
            open={openSection.guidance}
            onToggle={() => toggle("guidance")}
            testid="intel-section-guidance"
          >
            <ul className="space-y-1.5">
              {ctx.guidance_links.map((g) => (
                <li key={g.url}>
                  <a href={g.url} target="_blank" rel="noreferrer" className="text-[13px] text-[#0F766E] hover:underline flex items-center gap-1.5">
                    <BookOpen size={12}/>{g.title} <ExternalLink size={10}/>
                  </a>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ icon, title, subtitle, badge, open, onToggle, children, testid, warning }) {
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-white/60 transition"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${warning ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-[#064E2E]/10 text-[#064E2E]"}`}>{icon}</span>
          <div className="min-w-0">
            <p className="text-[11px] tracking-[0.18em] uppercase text-[#475569] font-medium">{title}</p>
            <p className="text-[13px] text-[#071A33] truncate">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {badge && badge !== "none" && (
            <span className="text-[9px] tracking-[0.1em] uppercase bg-[#0F766E]/10 text-[#0F766E] px-1.5 py-0.5 rounded">{badge.replace(/_/g, " ")}</span>
          )}
          {open ? <ChevronDown size={14} className="text-[#475569]"/> : <ChevronRight size={14} className="text-[#475569]"/>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}
